import { api } from './client';
import type { FileItem } from '../stores/fileStore';

interface ListFilesResponse {
  files: FileItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface UploadUrlResponse {
  uploadUrl: string;
  fileId: string;
  r2Key: string;
}

interface ConfirmUploadResponse {
  file: FileItem;
  ownerStorageUsed: number;
}

interface DownloadUrlResponse {
  downloadUrl: string;
}

export interface ListFilesParams {
  folderId?: string | null;
  search?: string;
  starred?: boolean;
  trashed?: boolean;
  page?: number;
  limit?: number;
  sortBy?: 'name' | 'size' | 'createdAt' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
}

export const filesApi = {
  list: async (params: ListFilesParams = {}) => {
    const searchParams = new URLSearchParams();

    // Handle folderId: send 'null' string for root folder, actual UUID for subfolders
    if (params.folderId !== undefined) {
      searchParams.set('folderId', params.folderId === null ? 'null' : params.folderId);
    }
    if (params.search) searchParams.set('search', params.search);
    if (params.starred) searchParams.set('starred', 'true');
    if (params.trashed !== undefined) searchParams.set('trashed', String(params.trashed));
    if (params.page) searchParams.set('page', String(params.page));
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);

    const url = `/files?${searchParams.toString()}`;
    console.log('Fetching files with URL:', url);
    const response = await api.get<ListFilesResponse>(url);
    console.log('Files API response:', JSON.stringify(response, null, 2));
    return response;
  },

  get: (id: string) => api.get<{ file: FileItem }>(`/files/${id}`),

  getUploadUrl: (data: {
    fileName: string;
    mimeType: string;
    size: number;
    folderId?: string | null;
  }) => api.post<UploadUrlResponse>('/files/upload-url', data),

  confirmUpload: (fileId: string) =>
    api.post<ConfirmUploadResponse>(`/files/${fileId}/confirm-upload`),

  getDownloadUrl: (id: string) =>
    api.get<DownloadUrlResponse>(`/files/${id}/download-url`),

  update: (id: string, data: Partial<{ name: string; folderId: string | null; isStarred: boolean }>) =>
    api.patch<{ file: FileItem }>(`/files/${id}`, data),

  delete: (id: string) => api.delete<{ success: boolean }>(`/files/${id}`),

  restore: (id: string) => api.post<{ success: boolean }>(`/files/${id}/restore`),

  deletePermanently: (id: string) =>
    api.delete<{ success: boolean; storageUsed: number }>(`/files/${id}/permanent`),

  bulkAction: (data: {
    fileIds: string[];
    action: 'delete' | 'restore' | 'move' | 'star' | 'unstar';
    folderId?: string;
  }) => api.post<{ success: boolean }>('/files/bulk-action', data),

  getContent: (id: string) => api.get<{ content: string }>(`/files/${id}/content`),

  updateContent: (id: string, content: string) =>
    api.put<{ file: FileItem }>(`/files/${id}/content`, { content }),
};
